/**
 * @fileoverview define CAAS-related data types
 */

type ImageUriFields = Partial<
  Record<
    | "square-caas"
    | "square_80-caas"
    | "square_560"
    | "square_480"
    | "square_320"
    | "square_80"
    | "square_560-caas"
    | "square_480-caas"
    | "square_320-caas"
    | "square_30_26"
    | "square_30_26-caas"
    | "wide"
    | "wide-caas"
    | "square"
    | "original"
    | "original-caas"
    | "portrait"
    | "landscape"
    | "landscape-caas"
    | "landscape480-caas"
    | "landscape1024-caas"
    | "landscape1024"
    | "freecrop"
    | "portrait-caas"
    | "freecrop-caas",
    {
      href_full?: string;
      url: string;
      width: number;
      height: number;
    }
  >
>;

interface ArticleSection {
  name: string;
  parent?: Pick<
    ArticleSection,
    "name" | "parent" | "uniqueName" | "directoryName"
  >;
  uniqueName: string;
  directoryName: string;
  // parameters: {};
}

interface ArticleTag {
  parent: ArticleTag | null;
  aliases: string[];
  name: string;
  urlPathHistory: string[] | null;
  id: string;
  type: string;
  fields: { key: string; value: string }[];
  uri: null;
  urlPath: string;
  sections: string[];
}

interface ArticleMedia {
  href: string;
  content: {
    id: string;
    type: string;
    source: string;
    sourceId: string;
    binaryLink: string;
    bucketName: string;
    alternates: unknown;
    fields: {
      caption: string;
      credit: string;
      com_escenic_defaultmetadata: { key: string; value: string }[];
    } & ImageUriFields;
  };
  summary: { key: string; value: string }[];
}

interface ArticleAttatchment {
  href: string;
  content: {
    id: string;
    type: string;
    title: string;
    href: string;
    source: string;
    sourceId: string;
    bucketName: string;
    attatchmentLink: string;
    binary: {
      url: string;
      filename: string;
    };
    fields: {
      caption: string;
      credit: string;
      byline: string;
    };
    "attachmentLink-caas": string;
  };
  summary: { key: string; value: string }[];
}

interface ArticleRelatedStory {
  href: string;
  content: {
    id: string;
    type: string;
    href: string;
    urlPath: string;
    source: string;
    sourceId: string;
    fields: { title: string };
  };
}

interface ArticleAuthor {
  sourceId: string;
  firstname: string;
  edited: string;
  surname: string;
  name: string;
  profiles?: { content: AuthorContext }[];
  id: string;
  source: string;
  updated: string;
  email: string;
  username: string;
}
[];

export enum ArticleElementTypeEnum {
  BOTTOM_PROFILE = "bottom_profile",
  BRIGHTCOVE_VIDEO = "brightcove_video",
  DYNAMIC_IFRAME_WRAPPER = "dynamic_iframe_wrapper",
  EMBED = "embed",
  EXTERNAL_LINK = "external_link",
  HEADLINE = "headline",
  HORIZONTAL_ROW = "horizontal_row",
  IMAGE = "image",
  INTERNAL_LINK = "internal_link",
  KICKER = "kicker",
  LIST_BULLETED = "list_bulleted",
  LIST_NUMBERED = "list_numbered",
  MAP = "map",
  MEDIA = "media",
  OMNY_PODCAST = "omny_podcast",
  PARAGRAPH = "paragraph",
  PODCAST_WRAPPER = "podcast_wrapper",
  PULL_QUOTE = "pull_quote",
  SLUG = "slug",
  STANDFIRST = "standfirst",
  SUB_HEAD = "sub_head",
  YOUTUBE_VIDEO = "youtube_video",
  RELATION = "relation",
  TABLE = "table",
  INTERVIEW = "interview",
  // DATA_WRAPPER = "data_wrapper", return as embed element,
}

interface ArticleElementRelation {
  id: string;
  source: string;
  sourceId: string;
  type: string;
  binaryLink: string;
  title?: string;
  href?: string;
  urlPath?: string;
  published?: string;
  fields: {
    caption: string;
    credit: string;
    com_escenic_defaultmetadata: { key: string; value: string }[];
  } & ImageUriFields;
  linkFollow?: ArticleElementRelation;
  alternates: unknown;
}

export interface CueRichTextNode {
  type: string;
  attributes?: unknown[];
  href?: string;
  target?: string;
  text?: string;
  children?: CueRichTextNode[];
}

interface ArticleRelatedContributorProfile {
  href: string;
  content?: {
    typeName: string;
    id: string;
    type: string;
    source: string;
    sourceId: string;
    updated: string;
    edited: string;
    urlPath?: string;
    urlPathHistory?: string[] | null;
    fields?: {
      name?: string;
      bio?: CueRichTextNode[];
      bio_raw?: string;
      email?: string;
      twitterHandle?: string;
      designation?: string;
      startdate?: string;
    };
    headshotImage: { content: { fields: ImageUriFields } }[];
  };
}

export interface ArticleElementField {
  name: string;
  value: string;
  annotations?: ArticleElementFieldAnnotation[];
  booleanValue?: boolean;
}

export interface ArticleElementFieldAnnotation {
  index: number;
  length: number;
  name: string;
  value: string;
}

export interface ArticleElement {
  id: string;
  type: ArticleElementTypeEnum;
  /**
   * Optional because some CaaS element types, such as `slug`,
   * do not contain a fields property.
   */
  fields?: ArticleElementField[];
  relation?: ArticleElementRelation;
  children?: string[];
}

export interface StoryQueueContext {
  typeName: string;
  id: string;
  href: string;
  urlPath: string;
  urlPathHistory?: string[] | null;
  type: string;
  source: string;
  sourceId: string;
  created: string;
  updated: string;
  expired: string | null;
  previewSourceId: string;
  authors: string[];
  fields: {
    title: string;
    displayTitle: string;
    body_raw: string;
    body: {
      type: string;
      attributes: unknown[];
      children: { type: string; text: string }[];
    }[];
  };
  storyQueueList: {
    content: {
      id: string;
      source: string;
      sourceId: string;
      type: string;
      fields: {
        // title: string;
      };
    };
  }[];
}
export interface ContentListContext {
  typeName: string;
  id: string;
  href: string;
  urlPath: string;
  urlPathHistory?: string[] | null;
  type: string;
  source: string;
  sourceId: string;
  created: string;
  updated: string;
  lastmodified: string;
  edited: string;
  title: string;
  sections: ArticleSection[];
  storyline?: string[];
  elements?: ArticleElement[];
}

export interface ArticleContext {
  typeName: string;
  id: string;
  href: string;
  urlPath: string;
  urlPathHistory?: string[] | null;
  type: string;
  source: string;
  sourceId: string;
  created: string;
  updated: string;
  lastmodified: string;
  edited: string | null;
  expired: string | null;
  scheduled: string | null;
  title: string;
  baseStory: null;
  previewSourceId: string;
  sections?: ArticleSection[];

  tags?: ArticleTag[];
  media?: ArticleMedia[];
  attachments: ArticleAttatchment[];
  relatedStories: ArticleRelatedStory[];

  relatedContributorProfile?: ArticleRelatedContributorProfile[];

  authors?: ArticleAuthor[];

  byline?: {
    customByline: string;
    translator: string;
  };
  sponsorship: {
    sponsorCheckbox: boolean;
    sponsorDateStart: string;
    sponsorDateEnd: string;
    sponsorName: string;
  };
  paywall: {
    printFlag: boolean;
    automateFlag: null;
    contentAccess: string;
    copyright: string;
    agencySource: string;
  };
  customBlurb: null;
  /**
   * for top articles, there is no field
   */
  storyContent?: {
    blurb: string;
    shortURL: string;
    stockCode: string;
  };
  displaySetting?: {
    zbsgFlag: boolean;
    zbcomFlag: boolean;
    hideAuthorFlag: boolean;
    displayHeadline: string;
    displayHeadlineZBCom: string;
    englishHeadline: string;
    englishBody: string;
    externalURL: string;
    displayType: string;
    displayAssets: string;
    hasAffiliateLabel: boolean;
    displayAssetsMultiple: string[];
    showRelatedLinksAndNewsletterSignupForm: boolean;
    hasUpdatedTimeDisplayed: boolean;
    noDuplication: boolean;
    hideFromBreakingNewsListing: boolean;
    hideFromSectionListing: boolean;
    sticky: boolean;
    publishToGoogleAMP: boolean;
    hideMedia: boolean;
    hideOutbrain: boolean;
    isWinnerPage: boolean;
  };
  campaignSetting: {
    campaignDisplay: string;
    campaignType: string;
  };
  creditInfo: {
    compiledBy: string;
    producer: string;
  };
  seoFields: {
    metaTitle: string;
    metaDescription: string;
    metaRobots: string;
    canonicalUrl: string;
  };
  englishVariantFields: {
    isTranslated: boolean;
  };
  ogFields: {
    ogTitle: string;
    ogDescription: string;
  };
  ogImages: { content: { fields: ImageUriFields } }[];
  cueFields: {
    noDuplication: boolean;
  };
  others?: {
    storyThread: string;
    storyThreadMetadata: string;
    storySource: string;
    storySourceMetadata: string;
    authoring: string;
    removeAuthor: boolean;
    editorialTag: string;
    customUrlAlias: boolean;
    urlAlias: string;
    urlHistory: string[];
  };
  /**
   * for top articles, there is no field
   */
  storyline?: string[];
  embedcodes: { id: string; html: string }[];
  elements: ArticleElement[];
  cookUrl: string;
  highlight?: Record<string, string[]>;
}

export interface AuthorContext {
  typeName: string;
  id: string;
  type: string;
  source: string;
  sourceId: string;
  updated: string;
  edited: string;
  urlPath: string;
  urlPathHistory?: string[] | null;
  fields: {
    name: string;
    bio?: CueRichTextNode[];
    shortBio?: CueRichTextNode[];
    bio_raw: string;
    email: string;
    twitterHandle: string;
    designation: string;
    startdate: string;
  };
  headshotImage?: { content: { fields: ImageUriFields } }[];
}

export interface SqsMessage {
  id: string;
  title: string;
  authors: string[];
  operator: string;
  homeSectionUrl: string;
  homeSection: string;
  source: string;
  sourceId: string;
  type: string;
  state: string;
  publication: string;
  url: string;
  cookUrl: string;
}
